import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { propType } from 'graphql-anywhere';
import { useHistory } from 'react-router-dom';

import { EMAIL_INTEGRATION_TYPES } from '../../constants-shared';

import { EllipsedLine } from '../../ui';

import withData from '../../decorators/withData';
import withObjectListContextProvider from '../../decorators/withObjectListContextProvider';
import withObjectLoader from '../../decorators/withObjectLoader';
import withPageTitle from '../../decorators/withPageTitle';
import withRecentView from '../../decorators/withRecentView';
import withRouter from '../../decorators/withRouter';
import withSearch from '../../decorators/withSearch';
import withSelectableContextProvider from '../../decorators/withSelectableContextProvider';
import withShareRedirect from '../../decorators/withShareRedirect';

import integrationItemFragment from '../../fragments/integrationItemFragment';
import integrationItemHierarchyFragment from '../../fragments/integrationItemHierarchyFragment';
import mainDisplayObjectFragment from '../../fragments/mainDisplayObjectFragment';

import QUERY_INTEGRATION_ITEM from '../../queries/queryIntegrationItem';
import QUERY_SEARCH_RESULTS_PAGE from '../../queries/querySearchResultsPage';

import { RightPanelProvider } from '../../contexts/RightPanelContext';

import composeWithNonDialogComponents from '../../utils/composeWithNonDialogComponents';
import formatDateTime from '../../utils/formatDateTime';
import getSegmentMainDisplayObjectVariables from '../../utils/getSegmentMainDisplayObjectVariables';

import Actions from '../Actions';
import { DataContext } from '../Data';
import FixedLayout from '../FixedLayout';
import GmailHelpMessagePopoverIcon from '../icons/GmailHelpMessagePopoverIcon';
import HierarchyBreadcrumbs from '../HierarchyBreadcrumbs';
import InfoPanel from '../InfoPanel';
import IntegrationsStatusBanner from '../IntegrationsStatusBanner';
import MainDisplayObjectDuplicateFilesIndicator from '../MainDisplayObjectDuplicateFilesIndicator';
import MainDisplayObjectDokkioTagBadge from '../MainDisplayObjectDokkioTagBadge';
import { mainDisplayObjectPath } from '../links/MainDisplayObjectLink';
import MainDisplayObjectPic from '../MainDisplayObjectPic';
import ObjectAutoActionIndicator from '../ObjectAutoActionIndicator';
import StarControl from '../StarControl';

import IntegrationItemFileContent, { SEGMENT_PAGE_CTXT as FILE_SEGMENT_PAGE_CTXT } from '../IntegrationItemFileContent';
import IntegrationItemFolderContent, { SEGMENT_PAGE_CTXT as FOLDER_SEGMENT_PAGE_CTXT } from '../IntegrationItemFolderContent';

export const QUERY_INTEGRATION_ITEM_HIERARCHY_RECURSIVE = gql`
  query integrationItem(
    $integrationID: DokkioID!
    $remoteID: ID!
  ) {
    integrationItem(
      integrationID: $integrationID
      remoteID: $remoteID
    ) {
      dokkioClass
      hierarchyRecursive {
        ...integrationItemHierarchyFragment
        integrationItem {
          ...integrationItemFragment
        }
      }
    }
  }
  ${integrationItemHierarchyFragment}
  ${integrationItemFragment}
`;

const DEFAULT_COLUMNS = [{
  columnType: 'name',
}, {
  columnType: 'modified_at',
}, {
  columnType: 'row_created_at',
}];

const DEFAULT_SLACK_COLUMNS = [{
  columnType: 'name',
}, {
  columnType: 'modified_at',
}, {
  columnType: 'slack_channels',
}];

export const IntegrationItemView = ({
  areAllObjectsLoaded,
  objects,
  reload,
  total,
}) => {
  const {
    account: { effectiveFeatures },
    integrationByID,
    integrationItem,
    integrationItem: {
      accountID,
      dokkioItem: { description, imageCaption },
      hierarchy,
      integrationItemType,
      isIntegrationRoot,
      integrationID,
      remoteID,
      shortcutRemoteID,
      shortcutIntegrationID,
    },
    integrationItemHierarchyRecursive,
    refetchIntegrationItem,
  } = useContext(DataContext);
  const { push, replace } = useHistory();
  const segmentPageCtxt = integrationItemType === 'integration_folder'
    ? FOLDER_SEGMENT_PAGE_CTXT
    : FILE_SEGMENT_PAGE_CTXT;
  const integration = integrationByID[integrationID] || {};
  const {
    integrationType,
    loadingFinishedAt,
    lastEmailSyncedSentAt,
  } = integration;
  const isSlack = integrationType.startsWith('slack');
  const isEmail = EMAIL_INTEGRATION_TYPES.includes(integrationType);
  const segmentAdditionalProps = {
    PageCtxt: segmentPageCtxt,
    ListType: integrationItemType === 'integration_folder' ? 'Folder' : undefined,
    ...getSegmentMainDisplayObjectVariables({
      integrationByID,
      mainDisplayObject: integrationItem,
    }),
  };

  useEffect(() => {
    if (shortcutRemoteID) {
      replace(`/a/${accountID}/integrations/${shortcutIntegrationID || integrationID}/remoteItems/${shortcutRemoteID}`);
    }
  }, []);


  const onMoveSuccess = (completedJobs) => {
    if (!completedJobs
      || completedJobs.length !== 1
      || completedJobs[0].integrationID !== integrationID
      || completedJobs[0].remoteID !== remoteID) {
      return;
    }

    const { newIntegrationItem } = completedJobs[0];

    if (newIntegrationItem.integrationID === integrationID
      && newIntegrationItem.remoteID === remoteID) {
      refetchIntegrationItem();
    } else {
      push(`/a/${accountID}${mainDisplayObjectPath(newIntegrationItem)}`);
    }
  };

  const reloadAll = () => {
    refetchIntegrationItem();
    if (reload) {
      reload();
    }
  };

  const renderObjectContextualActions = (targets, actionContext) => {
    const dropdownActions = [{
      action: 'open',
    }, {
      action: 'openOnDesktop',
    }, {
      action: 'openInNewTab',
    }, {
      action: 'openFolderLocation',
    }, {
      action: 'star',
    }, {
      action: 'share',
    }, {
      action: 'download',
    }, {
      action: 'rename',
    }, {
      action: 'unread',
    }, {
      action: 'setDokkioTags',
    }, {
      action: 'move',
    }, {
      action: 'copy',
    }, {
      action: 'editLogo',
    }, {
      action: 'delete',
    }];

    return (
      <Actions
        actionContext={actionContext}
        buttonActions={[]}
        dropdownActions={dropdownActions}
        onComplete={reload}
        onSuccess={reload}
        segmentPageCtxt={segmentPageCtxt}
        targets={targets}
      />
    );
  };

  const renderHeaderActions = () => {
    const isFile = integrationItemType === 'integration_file';
    const canCreateDriveFile = integrationType === 'google_drive';

    const buttonActions = [{
      action: 'share',
    },
    !isFile && !isEmail && {
      action: 'add',
      actions: [
        canCreateDriveFile && {
          action: 'createDriveFile',
          driveFileType: 'document',
          key: 'google-document',
        },
        canCreateDriveFile && {
          action: 'createDriveFile',
          driveFileType: 'spreadsheet',
          key: 'google-spreadsheet',
        },
        canCreateDriveFile && {
          action: 'createDriveFile',
          driveFileType: 'presentation',
          key: 'google-presentation',
        },
        canCreateDriveFile && {
          action: 'createDriveFile',
          driveFileType: 'drawing',
          key: 'google-drawing',
        },
        !isSlack && {
          action: 'createOfficeFile',
          officeFileType: 'document',
          key: 'office-document',
        },
        !isSlack && {
          action: 'createOfficeFile',
          officeFileType: 'spreadsheet',
          key: 'office-spreadsheet',
        },
        !isSlack && {
          action: 'createOfficeFile',
          officeFileType: 'presentation',
          key: 'office-presentation',
        },
        !isSlack && {
          action: 'createIntegrationFolder',
          onComplete: reloadAll,
        },
        {
          action: 'upload',
          segmentEventPrefix: 'Folder',
        },
      ].filter(Boolean),
    }, {
      action: 'viewOriginal',
    }, {
      action: 'open',
    }, {
      action: 'openOnDesktop',
    }].filter(Boolean);
    const dropdownActions = [{
      action: 'createTask',
    }, {
      action: 'star',
    }, {
      action: 'download',
    }, {
      action: 'rename',
    }, {
      action: 'setDokkioTags',
    }, {
      action: 'move',
      onSuccess: onMoveSuccess,
    }, {
      action: 'copy',
      onComplete: reload,
    }, {
      action: 'editLogo',
    }, {
      action: 'disconnect',
      onSuccess: () => replace(`/a/${accountID}/contentSource`),
    }, {
      action: 'delete',
      onSuccess: () => {
        const [{ parentRemoteID }] = hierarchy;
        replace(parentRemoteID || `/a/${accountID}`);
      },
    }];

    return (
      <Actions
        actionContext="pageHeader"
        buttonActions={buttonActions}
        dropdownActions={dropdownActions}
        onComplete={refetchIntegrationItem}
        onSuccess={reloadAll}
        segmentPageCtxt={segmentPageCtxt}
        targets={[integrationItem]}
      />
    );
  };

  let descriptionText = description;
  if (!description
    && isIntegrationRoot
    && loadingFinishedAt
    && lastEmailSyncedSentAt
  ) {
    descriptionText = `Goes back to ${formatDateTime(lastEmailSyncedSentAt)}`;
  }
  const hasCaptionDescription = !description
    && effectiveFeatures.includes('image_captions')
    && imageCaption;
  if (hasCaptionDescription) {
    descriptionText = imageCaption;
  }

  let bodyContent;
  if (integrationItemType === 'integration_folder') {
    bodyContent = (
      <IntegrationItemFolderContent
        areAllObjectsLoaded={areAllObjectsLoaded}
        objects={objects}
        parentIntegrationItem={integrationItem}
        reload={reload}
        renderObjectContextualActions={renderObjectContextualActions}
        segmentAdditionalProps={segmentAdditionalProps}
        total={total}
      />
    );
  } else {
    bodyContent = <IntegrationItemFileContent integrationItem={integrationItem} />;
  }

  return (
    <RightPanelProvider>
      <FixedLayout
        renderActions={renderHeaderActions}
        renderBadges={() => (
          <MainDisplayObjectDokkioTagBadge
            mainDisplayObject={integrationItem}
            segmentPageCtxt={segmentPageCtxt}
          />
        )}
        renderBanners={() => (integrationItemType === 'integration_folder'
          ? (
            <IntegrationsStatusBanner
              integrationID={integrationItem.integrationID}
              segmentPageCtxt={segmentPageCtxt}
            />
          )
          : null)}
        renderDescription={() => (
          <React.Fragment>
            <div className="flex items-center space-x-0.5">
              <EllipsedLine>{descriptionText}</EllipsedLine>
              {hasCaptionDescription && (
                <ObjectAutoActionIndicator
                  tooltip="This is an AI generated image caption"
                />
              )}
            </div>
            {!isIntegrationRoot && integrationItemHierarchyRecursive && (
              <HierarchyBreadcrumbs
                hierarchyRecursive={integrationItemHierarchyRecursive.hierarchyRecursive}
                segmentPageCtxt={segmentPageCtxt}
              />
            )}
          </React.Fragment>
        )}
        renderPanel={() => (
          <InfoPanel
            mainDisplayObject={integrationItem}
            onObjectsChanged={refetchIntegrationItem}
            searchName={integrationItem.name}
            segmentAdditionalProps={segmentAdditionalProps}
            segmentPageCtxt={segmentPageCtxt}
          />
        )}
        renderPic={picPx => (
          <MainDisplayObjectPic
            height={picPx}
            width={picPx}
            iconPx={picPx}
            mainDisplayObject={integrationItem}
            segmentPageCtxt={segmentPageCtxt}
          />
        )}
        renderTitle={() => (
          <React.Fragment>
            <EllipsedLine>
              {integrationItem.name}
            </EllipsedLine>
            <StarControl object={integrationItem} segmentPageCtxt={segmentPageCtxt} />
            <MainDisplayObjectDuplicateFilesIndicator
              className="w-4 h-4"
              mainDisplayObject={integrationItem}
            />
            <GmailHelpMessagePopoverIcon integrationItem={integrationItem} />
          </React.Fragment>
        )}
        segmentAdditionalProps={segmentAdditionalProps}
      >
        {bodyContent}
      </FixedLayout>
    </RightPanelProvider>
  );
};

IntegrationItemView.propTypes = {
  areAllObjectsLoaded: PropTypes.bool,
  objects: PropTypes.arrayOf(propType(mainDisplayObjectFragment)),
  reload: PropTypes.func,
  total: PropTypes.number,
};

IntegrationItemView.defaultProps = {
  areAllObjectsLoaded: false,
  objects: [],
  reload: () => null,
  total: 0,
};

export default composeWithNonDialogComponents(
  withShareRedirect,
  withRouter,
  withData(({ match: { params: { integrationID, remoteID } } }) => ({
    queries: [
      {
        query: QUERY_INTEGRATION_ITEM,
        variables: {
          integrationID,
          remoteID,
        },
      },
    ],
    showLoader: 'always',
  })),
  withData(({
    integrationItem,
    match: { params: { integrationID, remoteID } },
  }) => ({
    queries: [
      {
        name: 'integrationItemHierarchyRecursive',
        skip: integrationItem && integrationItem.isIntegrationRoot,
        query: QUERY_INTEGRATION_ITEM_HIERARCHY_RECURSIVE,
        variables: {
          integrationID,
          remoteID,
        },
      },
    ],
    showLoader: 'initial-load-only',
  })),
  withSearch(({ match: { params: { integrationID, remoteID } } }) => ({
    disabledFilters: ['integrationIDs', 'parentIntegrationItemIDs', 'integrationItemTypes'],
    builtinVariables: {
      parentIntegrationItemIDs: [{ integrationID, remoteID }],
    },
  })),
  withObjectListContextProvider(({ integrationByID, integrationItem }) => {
    let defaultColumns = DEFAULT_COLUMNS;

    const integration = integrationByID && integrationItem && integrationItem.integrationID
      ? integrationByID[integrationItem.integrationID]
      : null;
    if (integration && integration.integrationType) {
      if (integration.integrationType === 'slack') {
        defaultColumns = DEFAULT_SLACK_COLUMNS;
      }
    }

    return {
      listKey: 'folder',
      defaultOrderByColumn: 'modified_at',
      defaultOrderByDirection: 'desc',
      enabledOrderByColumns: ['name', 'bytes', 'modified_at', 'row_created_at'],
      defaultColumns,
    };
  }),
  withObjectLoader(QUERY_SEARCH_RESULTS_PAGE, {
    getObject: ({ mainDisplayObject }) => mainDisplayObject,
  }),
  withPageTitle(({ integrationItem: { name } = {} }) => name),
  withRecentView,
  withSelectableContextProvider({ segmentListType: 'Folder' }),
)(IntegrationItemView);
